import { cn } from '~/common/utils/styleUtil';
import { useTranslation } from 'react-i18next';
import { PRICECODE_WEIGHTCONTROLLED } from '~/common/constants/ppConstants';
import { formatPrice } from '~/common/utils/numberUtil';
import { Popover, PopoverContent, PopoverContentWithoutPortal, PopoverTrigger } from '~/common/components/ui/popover';
import { ChevronDown, Check } from 'lucide-react';
import type { ArticleChangeInfo } from '~/common/types/articleChangeInfo.ts';
import { useState, useEffect, useRef, useMemo } from 'react';

export interface ArticleSellUnitSelectorProps<
  T extends {
    articleNumber: string;
    isAction: boolean;
    pricePerSellUnit: number;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
    description: string;
  },
  TX extends {
    articleNumber: string;
    isAction: boolean;
    pricePerSellUnit: number;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
    description: string;
  }
> {
  selectedArticle: T;
  allArticles: TX[];
  onArticleChange?: (changeInfo: ArticleChangeInfo) => void;
  className?: string;
  showOnlyActionPrice?: boolean;
  size?: 'default' | 'lg';
  renderAsPortal?: boolean;
}

function SellUnitText<
  T extends {
    articleNumber: string;
    isAction: boolean;
    pricePerSellUnit: number;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
    description: string;
  }
>({ article, showPrice = true, size = 'default' }: Readonly<{ article: T; showPrice?: boolean; size?: 'default' | 'lg' }>) {
  const { t, i18n } = useTranslation();
  return (
    <>
      {showPrice && (
        <span
          className={cn('font-bold mr-1  whitespace-nowrap', size === 'lg' && 'text-xl')}
          data-testid={`sellunit-select-price-${article.articleNumber}`}
        >
          {`${formatPrice(article.pricePerSellUnit, i18n.language)} CHF /`}
        </span>
      )}
      <span
        className={`${size === 'lg' ? 'text-xl' : ''}`}
        data-testid={`sellunit-select-unit-${article.articleNumber}`}
      >
        {article.sellAmount > 1 ? (
          <>
            {article.sellUnit}
            {'\u00A0'}
            {t('sellUnitSeparator')}
            {'\u00A0'}
          </>
        ) : (
          ''
        )}
      </span>
      <span
        className={` whitespace-nowrap ${size === 'lg' ? 'text-xl' : ''}`}
        data-testid={`sellunit-select-amount${article.articleNumber}`}
      >
        {article.priceCode === PRICECODE_WEIGHTCONTROLLED ? (
          <span>{article.approxWeight?.toFixed(3)}</span>
        ) : (
          <span>{article.sellAmount}</span>
        )}{' '}
        <span>{article.unitText}</span>
      </span>
    </>
  );
}

export function ArticleSellUnit<
  T extends {
    articleNumber: string;
    isAction: boolean;
    pricePerSellUnit: number;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
    description: string;
    isHit?: boolean;
  },
  TX extends {
    articleNumber: string;
    isAction: boolean;
    pricePerSellUnit: number;
    sellUnit: string;
    sellAmount: number;
    priceCode: number;
    approxWeight?: number;
    unitText: string;
    description: string;
    isHit?: boolean;
  }
>({
  selectedArticle,
  allArticles,
  onArticleChange,
  className,
  showOnlyActionPrice = false,
  renderAsPortal = true,
  size = 'default'
}: Readonly<ArticleSellUnitSelectorProps<T, TX>>) {
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  let showPrice =
    (showOnlyActionPrice && selectedArticle.isAction && !selectedArticle.isHit) || showOnlyActionPrice === false;
  if (selectedArticle.isHit) {
    showPrice = true;
  }

  useEffect(() => {
    const handleScroll = () => {
      if (open) {
        setOpen(false);
      }
    };

    if (open) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const popoverContent = useMemo(
    () => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        className="max-h-96 overflow-auto"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {allArticles.map(article => {
          let showPriceForArticle =
            (showOnlyActionPrice && article.isAction && !article.isHit) || showOnlyActionPrice === false;
          if (article.isHit) {
            showPriceForArticle = true;
          }
          return (
            <button type="button"
              key={article.articleNumber}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();

                setOpen(false);

                if (onArticleChange) {
                  onArticleChange({
                    articleNumber: article.articleNumber,
                    articleDescription: article.description
                  });
                }
              }}
              className={cn(
                'relative flex w-full cursor-default select-none items-center py-2 pl-8 pr-2 text-sm ' +
                  'outline-hidden hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                'text-left hover:cursor-pointer'
              )}
            >
              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {selectedArticle.articleNumber === article.articleNumber && <Check className="h-4 w-4" />}
              </span>
              <SellUnitText article={article} size={size} showPrice={showPriceForArticle} />
            </button>
          );
        })}
      </div>
    ),
    [allArticles, selectedArticle, showOnlyActionPrice, size, onArticleChange]
  );

  if (allArticles.length < 2) {
    return (
      <div className={cn('pb-2', className)}>
        <SellUnitText showPrice={showPrice} size={size} article={selectedArticle} />
      </div>
    );
  }

  return (
    <div className={'pb-2'}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button"
            ref={triggerRef}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
              'border-primary',
              className
            )}
            data-testid={`sellunit-select-${selectedArticle.articleNumber}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <span className="flex-1 text-left">
              <SellUnitText article={selectedArticle} size={size} showPrice={showPrice} />
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        {renderAsPortal ? (
          <PopoverContent className="p-0" align="start" style={{ width: triggerWidth }}>
            {popoverContent}
          </PopoverContent>
        ) : (
          <PopoverContentWithoutPortal className="p-0" align="start" style={{ width: triggerWidth }}>
            {popoverContent}
          </PopoverContentWithoutPortal>
        )}
      </Popover>
    </div>
  );
}
